/**
 * Canary probe — side-effect module, imported once at the top of main.ts.
 *
 * On boot, if the app is running on an UNAUTHORIZED origin (see canary-origin.ts),
 * it fires a single fire-and-forget beacon to the backend so Martin is alerted
 * that a build is deployed where it should not be. On an authorized origin it
 * does nothing at all — no network, no storage.
 *
 * Hard rules:
 *  - Never throws. A theft alarm must not be able to break app bootstrap.
 *  - Browser only; skips the Karma test runner explicitly.
 *  - At most one beacon per browser session.
 *  - `text/plain` via sendBeacon to avoid a CORS preflight (the endpoint accepts
 *    any origin and answers 204).
 */
import { environment } from 'environments/environment';
import {
  CANARY_BEACON_ID,
  buildCanaryBeaconUrl,
  isAuthorizedCanaryOrigin,
} from './canary-origin';

const SESSION_FLAG = 'proporio_canary_reported';

function runCanaryProbe(): void {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;

    // Never phone home from the Karma test runner (same guard app.config uses).
    if ((window as unknown as { __karma__?: unknown }).__karma__) return;

    const projectId = environment?.firebase?.projectId ?? '';
    const hostname = window.location?.hostname ?? '';
    if (isAuthorizedCanaryOrigin(hostname, projectId)) return;

    // One beacon per session. If storage is blocked (private mode), fall through
    // and beacon once per page load — degraded but still passive.
    try {
      if (sessionStorage.getItem(SESSION_FLAG)) return;
      sessionStorage.setItem(SESSION_FLAG, '1');
    } catch {
      /* storage unavailable — proceed best-effort */
    }

    const url = buildCanaryBeaconUrl(environment.firebase);
    const payload = JSON.stringify({
      id: CANARY_BEACON_ID,
      origin: window.location.origin,
      href: window.location.href,
      referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
      reason: 'foreign_origin',
    });

    if (typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, new Blob([payload], { type: 'text/plain' }));
    } else {
      void fetch(url, {
        method: 'POST',
        body: payload,
        keepalive: true,
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain' },
      }).catch(() => undefined);
    }
  } catch {
    /* a beacon must never break the app */
  }
}

runCanaryProbe();
